
import React, { useState } from "react";
import { Table, TableHeader } from "@/components/ui/table";
import { AdminUser, SortField } from "@/hooks/admin/users/types";
import { UserRole, StrategyProvider } from "@/types/strategy-types";
import UserTableHeader from "./UserTableHeader";
import UserTableFilters from "./UserTableFilters";
import UsersTableContent from "./UsersTableContent";
import { AddUserButton } from "../users/AddUserButton";
import DeleteUserDialog from "./DeleteUserDialog";
import { getUserDeleteInfo, UserDeleteInfo } from "@/utils/deleteUserUtils";
import { DeleteUserPopup } from "@/components/user/DeleteUserPopup";

interface UsersTableProps {
  users: AdminUser[];
  strategies: any[];
  providers: StrategyProvider[];
  onUpdateRole: (roleId: string, newRole: UserRole) => Promise<void>;
  onUpdateUserName: (userId: string, firstName: string, lastName: string) => Promise<void>;
  onDeleteUser: (userId: string) => Promise<void>;
  sortField?: SortField;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: SortField) => void;
}

const UsersTable = ({ 
  users, 
  strategies, 
  providers, 
  onUpdateRole,
  onUpdateUserName,
  onDeleteUser,
  sortField,
  sortOrder,
  onSort
}: UsersTableProps) => {
  // response will be either the delete-info object or null
  const [response, setResponse] = useState<UserDeleteInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Filter users based on search term and role filter
  const filteredUsers = users.filter(user => {
    // Search filter (case insensitive)
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Role filter
    const matchesRole = !roleFilter || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Handler functions
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleRoleFilterChange = (role: string | null) => {
    setRoleFilter(role);
  };

  const handleStartEdit = (user: AdminUser) => {
    setEditingUser(user.id);
    setEditFirstName(user.first_name || '');
    setEditLastName(user.last_name || '');
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditFirstName('');
    setEditLastName('');
  };

  const handleSaveEdit = async (userId: string) => {
    await onUpdateUserName(userId, editFirstName, editLastName);
    setEditingUser(null);
  };

  const handleToggleExpand = (userId: string) => {
    setExpandedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDeleteUser = async (userId: string) => {
    setUserToDelete(userId);

    const info: UserDeleteInfo = await getUserDeleteInfo(userId);
          setResponse(info);
  };

  const confirmDeleteUser = async () => {
    if (userToDelete) {
      await onDeleteUser(userToDelete);
      setUserToDelete(null);
      setResponse(null);
    }
  };

  const onClose = (): void => {
    setResponse(null);
  };

  const onConfirm = async () => {
    // invoke actual delete action here, e.g. call deleteUser(userId)
    console.log("Confirmed deletion for user", userToDelete);

    await onDeleteUser(userToDelete);
    setUserToDelete(null);
    setResponse(null);
  };

  // Helper functions for expanded rows
  const getProviderStrategies = (providerId: string | undefined) => {
    if (!providerId) return [];
    return strategies.filter(strategy => 
      strategy.provider_id === providerId
    );
  };

  const getAssignedProvider = (providerId: string | undefined) => {
    if (!providerId) return null;
    return providers.find(provider => provider.id === providerId) || null;
  };

  const handleTableRefresh = () => {
    // This function will be called after a user is added
    if (onUpdateRole) {
      // Calling onUpdateRole with the same values triggers a reload
      const dummyUserId = "refresh-trigger";
      const dummyRole = UserRole.User;
      onUpdateRole(dummyUserId, dummyRole).catch(console.error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <h2 className="text-2xl font-bold">Users Management</h2>
        <AddUserButton onUserAdded={handleTableRefresh} />
      </div>
      
      <UserTableFilters 
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        roleFilter={roleFilter}
        onRoleFilterChange={handleRoleFilterChange}
      />
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <UserTableHeader 
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          </TableHeader>
          
          <UsersTableContent 
            filteredUsers={filteredUsers}
            strategies={strategies}
            providers={providers}
            expandedUsers={expandedUsers}
            editingUser={editingUser}
            editFirstName={editFirstName}
            editLastName={editLastName}
            onToggleExpand={handleToggleExpand}
            onEditFirstNameChange={setEditFirstName}
            onEditLastNameChange={setEditLastName}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSaveEdit={handleSaveEdit}
            onUpdateRole={onUpdateRole}
            onUpdateName={onUpdateUserName}
            onDeleteUser={handleDeleteUser}
            getProviderStrategies={getProviderStrategies}
            getAssignedProvider={getAssignedProvider}
          />
        </Table>
      </div>
      
      {/* Delete User Confirmation Dialog */}
      
{response !== null && (
          <DeleteUserPopup
            userId={userToDelete}
            userName={response.userName}
            isOpen={true}
            userStatus={{
              hasStripeSub: response.has_active_subscription,
              hasReferrals: response.has_referrals,
              isOnTrial: response.has_trials,
            }}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        )}
      
      {/*userToDelete && (
        <DeleteUserDialog
          isOpen={!!userToDelete}
          onClose={() => setUserToDelete(null)}
          onConfirm={confirmDeleteUser}
        />
      )*/}
    </div>
  );
};

export default UsersTable;
